import { Capacitor } from '@capacitor/core';

// Device-side driver for over-the-air web bundle updates (Phase 2).
//
// It talks to the Phase 1 backend (`GET /api/app-updates/latest` +
// `/bundles/:file?token=`) and to `@capgo/capacitor-updater` in manual mode.
// We access the native plugins through `window.Capacitor.Plugins.*` (the same
// pattern as customerDisplayBridge) instead of importing the npm packages, so
// the web build compiles even before the plugins are installed. On web/desktop
// every method is a safe no-op; on device (after `npm i` + `cap sync`) the
// plugins register on the global and it all lights up.
//
// Everything is injectable via `createOtaUpdater(deps)` so the pure
// check -> download -> apply flow is unit-testable without a device.

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.boltin.com';

const nativePlugin = (name) => {
  if (!Capacitor.isNativePlatform()) return null;
  return (typeof window !== 'undefined' && window.Capacitor?.Plugins?.[name]) || null;
};

const defaultTarget = () =>
  (process.env.REACT_APP_APP_TARGET === 'handheld' ? 'handheld' : 'pos');

export const createOtaUpdater = (deps = {}) => {
  const {
    updater = nativePlugin('CapacitorUpdater'),
    device = nativePlugin('Device'),
    app = nativePlugin('App'),
    fetchFn = typeof fetch === 'function' ? (...args) => fetch(...args) : null,
    apiBase = API_BASE,
    apiKey = process.env.REACT_APP_OTA_API_KEY || '',
    target = defaultTarget(),
    log = console,
  } = deps;

  // A bundle that was downloaded but not yet applied because the POS was busy.
  // Held in memory so the next idle tick applies it without re-downloading.
  let pendingBundle = null;

  // We can only run when the native plugins are present, we have a fetcher, and
  // a fleet read-only API key was provisioned at build time.
  const isSupported = () => Boolean(updater && device && app && fetchFn && apiKey);

  // Ask the backend whether a newer bundle exists for this device.
  // Returns the update payload ({ version, url, checksum, ... }) or null.
  const checkForUpdate = async () => {
    const { identifier: deviceId } = (await device.getId()) || {};
    const current = (await updater.current()) || {};
    const currentVersion = current.bundle?.version || '0.0.0';
    const info = (await app.getInfo()) || {};
    const currentNativeVersion = info.version || '';

    const query = new URLSearchParams({
      target,
      platform: 'android',
      currentVersion,
      currentNativeVersion,
      deviceId: deviceId || '',
    });

    const res = await fetchFn(`${apiBase}/api/app-updates/latest?${query.toString()}`, {
      headers: { 'X-API-Key': apiKey },
    });
    if (!res.ok) {
      log.warn?.(`[ota] /latest returned ${res.status}`);
      return null;
    }
    const data = await res.json();
    return data && data.updateAvailable ? data : null;
  };

  // Download + integrity-check the bundle. The `url` from /latest already
  // carries the short-lived signed ?token=; prefix the API base. The plugin
  // verifies the sha256 `checksum`.
  const download = (update) =>
    updater.download({
      url: `${apiBase}${update.url}`,
      version: update.version,
      checksum: update.checksum,
    });

  // Swap the bundle in — this reloads the webview into the new version.
  const apply = (bundle) => updater.set(bundle);

  // One cycle: apply an already-staged bundle if we're now idle, otherwise
  // check for a new one, download it, and apply immediately if idle or stage
  // it for the next idle tick.
  const checkAndStage = async (isIdle = () => true) => {
    if (!isSupported()) return { status: 'unsupported' };

    try {
      // A bundle downloaded on an earlier (busy) tick — apply it once idle.
      if (pendingBundle) {
        if (!isIdle()) return { status: 'staged', version: pendingBundle.version };
        const bundle = pendingBundle;
        pendingBundle = null;
        await apply(bundle);
        return { status: 'applied', version: bundle.version, deferred: true };
      }

      const update = await checkForUpdate();
      if (!update) return { status: 'up-to-date' };

      const bundle = await download(update);
      // Carry the version forward for the staged/deferred-apply case.
      if (bundle && bundle.version == null) bundle.version = update.version;

      if (isIdle()) {
        await apply(bundle);
        return { status: 'applied', version: update.version };
      }

      pendingBundle = bundle;
      return { status: 'staged', version: update.version };
    } catch (err) {
      log.warn?.('[ota] update check failed', err);
      return { status: 'error', error: err };
    }
  };

  return {
    isSupported,
    checkForUpdate,
    download,
    apply,
    checkAndStage,
    hasPending: () => Boolean(pendingBundle),
  };
};

// Default instance wired to the real Capacitor plugins + environment.
export const otaUpdater = createOtaUpdater();
